import { useState, useEffect } from 'react';
import api, { getShortUrl, getPlatformDomains } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import {
  Sparkle, Link as LinkIcon, CaretDown, Lock, Clock, Crosshair,
  Megaphone, Tag, Lightning, CheckCircle, XCircle, Globe, QrCode,
  Eye, DotsThree, EyeSlash, Prohibit, Folder,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import QRCodePreview from './QRCodePreview';
import CustomLinkPreview from './CustomLinkPreview';

// ── Preview panel tabs ────────────────────────────────────────────────────────
const PREVIEW_TABS = [
  { id: 'qr',  label: 'QR Code',    icon: QrCode },
  { id: 'og',  label: 'Link Preview', icon: Eye },
];

export default function CreateLinkModal({ open, onClose, onCreated }) {
  const { workspace } = useAuth();

  // Core fields
  const [url, setUrl]         = useState('');
  const [slug, setSlug]       = useState('');
  const [title, setTitle]     = useState('');
  const [tags, setTags]       = useState('');
  const [comments, setComments] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');

  // Advanced toggles (F10 overflow menu)
  const [cloaked, setCloaked]     = useState(false);
  const [indexable, setIndexable] = useState(true);
  const [conversionTracking, setConversionTracking] = useState(false);

  // Existing advanced fields
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expiryDate, setExpiryDate]     = useState('');
  const [maxClicks, setMaxClicks]       = useState('');
  const [password, setPassword]         = useState('');
  const [utmSource, setUtmSource]       = useState('');
  const [utmMedium, setUtmMedium]       = useState('');
  const [utmCampaign, setUtmCampaign]   = useState('');
  const [utmTerm, setUtmTerm]           = useState('');
  const [utmContent, setUtmContent]     = useState('');
  const [ctaOverlayId, setCtaOverlayId] = useState('');
  const [selectedPixels, setSelectedPixels] = useState([]);

  // Custom OG / link preview (F7)
  const [ogData, setOgData] = useState({});

  // Domain selector data (F5/B11)
  const [availableDomains, setAvailableDomains] = useState([]);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [folderId, setFolderId] = useState('none');

  // Remote data
  const [availableCtas, setAvailableCtas]     = useState([]);
  const [availablePixels, setAvailablePixels] = useState([]);
  const [utmPresets, setUtmPresets]           = useState([]);

  // AI
  const [aiSlugs, setAiSlugs]   = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTitle, setAiTitle]   = useState('');

  // UI state
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [creating, setCreating]           = useState(false);
  const [previewTab, setPreviewTab]       = useState('qr');

  // ── Load workspace data when modal opens ─────────────────────────────────
  useEffect(() => {
    if (!open || !workspace?.workspace_id) return;
    const wsId = workspace.workspace_id;

    api.get(`/workspaces/${wsId}/ctas`).then(r => setAvailableCtas(r.data)).catch(() => {});
    api.get(`/workspaces/${wsId}/pixels`).then(r => setAvailablePixels(r.data)).catch(() => {});
    api.get(`/workspaces/${wsId}/utm-presets`).then(r => setUtmPresets(r.data)).catch(() => {});
    api.get(`/workspaces/${wsId}/folders`).then(r => setAvailableFolders(r.data || [])).catch(() => setAvailableFolders([]));

    // Load available domains (B11)
    loadAvailableDomains(wsId).then(setAvailableDomains).catch(() => {});
  }, [open, workspace?.workspace_id]);

  // Set default domain when list loads — prefer workspace's chosen platform domain
  useEffect(() => {
    if (availableDomains.length > 0 && !selectedDomain) {
      const preferred = workspace?.default_short_domain;
      const match = preferred && availableDomains.find(d => d.domain === preferred);
      setSelectedDomain(match ? match.domain : availableDomains[0].domain);
    }
  }, [availableDomains, selectedDomain, workspace?.default_short_domain]);

  // ── AI slug suggestions ───────────────────────────────────────────────────
  useEffect(() => {
    if (!url || !url.startsWith('http')) { setAiSlugs([]); setAiTitle(''); return; }
    const timer = setTimeout(async () => {
      try {
        setAiLoading(true);
        const res = await api.post('/ai/suggest-slugs', { destination_url: url });
        setAiSlugs(res.data.slugs || []);
        if (res.data.metadata?.title) {
          setAiTitle(res.data.metadata.title);
          if (!title) setTitle(res.data.metadata.title);
        }
      } catch { setAiSlugs([]); } finally { setAiLoading(false); }
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // ── Slug availability check ───────────────────────────────────────────────
  useEffect(() => {
    if (!slug) { setSlugAvailable(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/links/check-slug', { slug });
        setSlugAvailable(res.data.available);
      } catch { setSlugAvailable(null); }
    }, 400);
    return () => clearTimeout(timer);
  }, [slug]);

  // ── Create link ───────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!url) { toast.error('Please enter a destination URL'); return; }
    try {
      setCreating(true);
      const payload = {
        destination_url: url,
        title,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        comments,
        cloaked,
        indexable,
        conversion_tracking: conversionTracking,
        og_title: ogData?.default?.title || null,
        og_description: ogData?.default?.description || null,
        og_image: ogData?.default?.image || null,
        og_overrides: buildOgOverrides(ogData),
      };
      if (slug) payload.slug = slug;
      if (selectedDomain) {
        payload.domain = selectedDomain;        // used by edge function for custom domains
        payload.short_domain = selectedDomain;  // stored on the link record for URL building
      }
      if (expiryDate) payload.expiry_date = new Date(expiryDate).toISOString();
      if (maxClicks) payload.max_clicks = parseInt(maxClicks);
      if (password) payload.password = password;
      const utm = {};
      if (utmSource) utm.source = utmSource;
      if (utmMedium) utm.medium = utmMedium;
      if (utmCampaign) utm.campaign = utmCampaign;
      if (utmTerm) utm.term = utmTerm;
      if (utmContent) utm.content = utmContent;
      if (Object.keys(utm).length > 0) payload.utm_params = utm;
      if (ctaOverlayId && ctaOverlayId !== 'none') payload.cta_overlay_id = ctaOverlayId;
      if (selectedPixels.length > 0) payload.retargeting_pixels = selectedPixels;
      if (folderId && folderId !== 'none') payload.folder_id = folderId;

      const res = await api.post('/links', payload);
      const shortUrl = getShortUrl(res.data);
      try { await navigator.clipboard.writeText(shortUrl); } catch { /* fallback */ }
      toast.success('Link created — short URL copied to clipboard');
      onCreated(res.data);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setUrl(''); setSlug(''); setTitle(''); setTags(''); setComments('');
    setShowAdvanced(false); setExpiryDate(''); setMaxClicks(''); setPassword('');
    setUtmSource(''); setUtmMedium(''); setUtmCampaign(''); setUtmTerm(''); setUtmContent('');
    setCtaOverlayId(''); setSelectedPixels([]); setAiSlugs([]); setAiTitle('');
    setSlugAvailable(null); setOgData({}); setCloaked(false); setIndexable(true);
    setConversionTracking(false); setSelectedDomain(''); setFolderId('none');
  };

  const previewUrl = slug
    ? getShortUrl(slug, selectedDomain || undefined)
    : getShortUrl('your-slug', selectedDomain || undefined);
  let destinationHost = '';
  try { if (url) destinationHost = new URL(url).hostname; } catch { /* noop */ }

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); resetForm(); } }}>
      <DialogContent className="sm:max-w-4xl rounded-2xl border border-border max-h-[94vh] overflow-hidden p-0 gap-0 flex flex-col font-['Geist',system-ui,sans-serif]">

        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl gradient-primary text-white shadow-glow">
                <LinkIcon size={18} weight="fill" />
              </span>
              <div>
                <DialogTitle className="font-['Instrument_Serif',serif] text-2xl font-normal tracking-tight text-foreground">
                  Create new link
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Paste a URL — AI will suggest slugs and metadata.
                </p>
              </div>
            </div>

            {/* Advanced overflow menu (F10) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-9 w-9 grid place-items-center rounded-lg border border-border bg-white text-muted-foreground hover:text-foreground hover:bg-muted/50 transition" title="Advanced options">
                  <DotsThree size={18} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={() => setCloaked(!cloaked)}
                  className={cloaked ? 'text-primary font-semibold' : ''}
                >
                  <EyeSlash size={14} className="mr-2" />
                  Link Cloaking {cloaked ? '✓' : ''}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIndexable(!indexable)}
                  className={!indexable ? 'text-primary font-semibold' : ''}
                >
                  <Prohibit size={14} className="mr-2" />
                  Search Engine Indexing {!indexable ? '(off) ✓' : '(on)'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setConversionTracking(!conversionTracking)} className={conversionTracking ? 'text-primary font-semibold' : ''}>
                  <Lightning size={14} className="mr-2" />
                  Conversion Tracking {conversionTracking ? '✓' : ''}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Active advanced badges */}
          {(cloaked || !indexable || conversionTracking) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {cloaked && <span className="chip bg-amber-50 text-amber-700"><EyeSlash size={10} /> Cloaked</span>}
              {!indexable && <span className="chip bg-rose-50 text-rose-600"><Prohibit size={10} /> Noindex</span>}
              {conversionTracking && <span className="chip bg-emerald-50 text-emerald-700"><Lightning size={10} /> Conversion tracking</span>}
            </div>
          )}
        </DialogHeader>

        {/* Body — 2-column grid */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid md:grid-cols-[1fr_300px]">
            {/* ── Left: form panel ── */}
            <div className="px-6 py-5 space-y-5">
              {/* Destination */}
              <Section title="Destination" icon={Globe}>
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://example.com/your-page"
                  className="input-modern"
                  data-testid="create-url-input"
                  autoFocus
                />
              </Section>

              {/* AI suggestions */}
              {(aiSlugs.length > 0 || aiLoading) && (
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkle size={14} weight="fill" className="text-primary" />
                    <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-primary font-['Geist_Mono',ui-monospace,monospace]">AI suggestions</span>
                    {aiLoading && <span className="text-[10px] text-muted-foreground animate-pulse">generating…</span>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {aiSlugs.map(s => (
                      <button key={s} onClick={() => setSlug(s)} data-testid={`ai-slug-${s}`}
                        className={`rounded-xl px-3 py-1.5 text-xs font-medium transition-all ${slug === s ? 'bg-primary text-white shadow-glow' : 'bg-white border border-border text-foreground hover:border-primary/50'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Short Link row — domain selector (F5) + slug */}
              <div>
                <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-1.5 mb-2 font-['Geist_Mono',ui-monospace,monospace]">
                  <LinkIcon size={12} /> Short Link
                </span>
                <div className="flex items-stretch gap-0 rounded-xl border border-border overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-shadow bg-white">
                  {/* Domain selector */}
                  <select
                    value={selectedDomain}
                    onChange={e => setSelectedDomain(e.target.value)}
                    className="h-10 pl-3 pr-2 text-xs font-medium text-foreground bg-muted/40 border-r border-border focus:outline-none"
                    data-testid="domain-selector"
                  >
                    {availableDomains.map(d => (
                      <option key={d.domain} value={d.domain}>{d.domain}</option>
                    ))}
                    {availableDomains.length === 0 && (
                      <option value="">linkflow.app</option>
                    )}
                  </select>
                  <span className="flex items-center px-1.5 text-muted-foreground text-xs">/</span>
                  {/* Slug input */}
                  <div className="relative flex-1">
                    <input
                      value={slug}
                      onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="my-custom-slug"
                      className="w-full h-10 pr-20 pl-2 text-sm bg-transparent focus:outline-none"
                      data-testid="create-slug-input"
                    />
                    {slug && slugAvailable !== null && (
                      <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 chip ${slugAvailable ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                        {slugAvailable ? <><CheckCircle size={11} weight="fill" />Available</> : <><XCircle size={11} weight="fill" />Taken</>}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Title + Tags row */}
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Title">
                  <input value={title} onChange={e => setTitle(e.target.value)} placeholder={aiTitle || 'Spring launch'} className="input-modern" data-testid="create-title-input" />
                </Field>
                <Field label="Tags" icon={Tag} hint="comma separated">
                  <input value={tags} onChange={e => setTags(e.target.value)} placeholder="marketing, q1, ads" className="input-modern" data-testid="create-tags-input" />
                </Field>
              </div>

              <Field label="Folder" icon={Folder} hint="optional — organise in Library">
                <Select value={folderId} onValueChange={setFolderId}>
                  <SelectTrigger className="h-10 rounded-xl bg-white" data-testid="create-folder-select">
                    <SelectValue placeholder="No folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No folder</SelectItem>
                    {availableFolders.map((f) => (
                      <SelectItem key={f.folder_id} value={f.folder_id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {/* Comments (F9) */}
              <Field label="Comments (internal note)">
                <input value={comments} onChange={e => setComments(e.target.value)} placeholder="Add a note for your team…" className="input-modern" data-testid="create-comments-input" />
              </Field>

              {/* Advanced toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                data-testid="toggle-advanced"
              >
                <CaretDown size={12} weight="bold" className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                {showAdvanced ? 'Hide' : 'Show'} advanced options
              </button>

              {showAdvanced && (
                <div className="rounded-2xl border border-border bg-muted/40 p-5 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Expiry date" icon={Clock}>
                      <input type="datetime-local" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} className="input-modern" data-testid="create-expiry-input" />
                    </Field>
                    <Field label="Max clicks">
                      <input type="number" value={maxClicks} onChange={e => setMaxClicks(e.target.value)} placeholder="e.g. 1000" className="input-modern" data-testid="create-max-clicks-input" />
                    </Field>
                  </div>
                  <Field label="Password protection" icon={Lock}>
                    <input type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="Leave empty for none" className="input-modern" data-testid="create-password-input" />
                  </Field>

                  {/* UTM */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5"><Crosshair size={12} /> UTM parameters</span>
                      {utmPresets.length > 0 && (
                        <Select onValueChange={presetId => {
                          const p = utmPresets.find(x => x.preset_id === presetId);
                          if (p) { setUtmSource(p.source||''); setUtmMedium(p.medium||''); setUtmCampaign(p.campaign||''); setUtmTerm(p.term||''); setUtmContent(p.content||''); }
                        }}>
                          <SelectTrigger className="h-8 w-[150px] rounded-lg bg-white text-xs" data-testid="create-utm-preset-select"><SelectValue placeholder="Load preset…" /></SelectTrigger>
                          <SelectContent>
                            {utmPresets.map(p => <SelectItem key={p.preset_id} value={p.preset_id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[
                        { v: utmSource, set: setUtmSource, p: 'source', test: 'create-utm-source' },
                        { v: utmMedium, set: setUtmMedium, p: 'medium', test: 'create-utm-medium' },
                        { v: utmCampaign, set: setUtmCampaign, p: 'campaign', test: 'create-utm-campaign' },
                        { v: utmTerm, set: setUtmTerm, p: 'term', test: 'create-utm-term' },
                        { v: utmContent, set: setUtmContent, p: 'content', test: 'create-utm-content' },
                      ].map(f => (
                        <input key={f.p} value={f.v} onChange={e => f.set(e.target.value)} placeholder={f.p} className="input-modern h-9 text-xs" data-testid={f.test} />
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  {availableCtas.length > 0 && (
                    <Field label="CTA overlay" icon={Megaphone}>
                      <Select value={ctaOverlayId} onValueChange={setCtaOverlayId}>
                        <SelectTrigger className="h-10 rounded-xl bg-white" data-testid="create-cta-select"><SelectValue placeholder="None (no CTA)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {availableCtas.map(c => <SelectItem key={c.cta_id} value={c.cta_id}>{c.name} — {c.headline}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}

                  {/* Pixels */}
                  {availablePixels.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5 mb-2"><Crosshair size={12} /> Retargeting pixels</span>
                      <div className="grid sm:grid-cols-2 gap-2" data-testid="create-pixels-list">
                        {availablePixels.map(pixel => {
                          const checked = selectedPixels.includes(pixel.pixel_id);
                          return (
                            <label key={pixel.pixel_id} className={`flex items-center gap-2.5 cursor-pointer rounded-xl border p-3 transition-all ${checked ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40'}`} data-testid={`pixel-option-${pixel.pixel_id}`}>
                              <input type="checkbox" checked={checked} onChange={() => setSelectedPixels(prev => checked ? prev.filter(id => id !== pixel.pixel_id) : [...prev, pixel.pixel_id])} className="h-4 w-4 rounded accent-primary cursor-pointer shrink-0" />
                              <span className="text-sm font-medium text-foreground truncate">{pixel.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Right: preview pane ── */}
            <aside className="hidden md:flex flex-col border-l border-border bg-muted/30">
              {/* Preview tab switcher */}
              <div className="px-4 pt-4">
                <div className="flex gap-1 bg-muted rounded-lg p-1 mb-4">
                  {PREVIEW_TABS.map(({ id, label, icon: Icon }) => (
                    <button key={id} type="button" onClick={() => setPreviewTab(id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-all ${previewTab === id ? 'bg-white text-foreground shadow-soft' : 'text-muted-foreground hover:text-foreground'}`}>
                      <Icon size={12} weight={previewTab === id ? 'fill' : 'regular'} />{label}
                    </button>
                  ))}
                </div>

                {previewTab === 'qr' && (
                  <div className="space-y-4">
                    {/* Short URL preview */}
                    <div className="rounded-2xl border border-border bg-white p-4 shadow-soft">
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-rose-300" />
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-300" />
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                        <span className="ml-2 font-mono">preview</span>
                      </div>
                      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-1">Short URL</p>
                      <p className="font-mono text-xs font-semibold text-foreground break-all">{previewUrl}</p>
                      {destinationHost && (
                        <span className="chip bg-primary/10 text-primary mt-3"><Globe size={10} weight="fill" />{destinationHost}</span>
                      )}
                    </div>

                    {/* Inline QR preview (F6) */}
                    <div className="rounded-2xl border border-border bg-white p-4 shadow-soft flex flex-col items-center gap-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground self-start">QR Code</p>
                      <QRCodePreview value={slug ? previewUrl : ''} size={120} />
                      {!slug && <p className="text-xs text-muted-foreground text-center">Enter a slug to generate QR</p>}
                    </div>
                  </div>
                )}

                {previewTab === 'og' && (
                  <div className="rounded-2xl border border-border bg-white p-4 shadow-soft">
                    <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-3">Custom Link Preview</p>
                    <CustomLinkPreview value={ogData} onChange={setOgData} />
                  </div>
                )}
              </div>

              {/* Pro tip */}
              <div className="px-4 pb-4 mt-auto">
                <div className="surface-card p-4">
                  <div className="flex items-center gap-2">
                    <span className="grid place-items-center h-8 w-8 rounded-xl bg-primary/10 text-primary">
                      <Lightning size={14} weight="fill" />
                    </span>
                    <p className="text-xs text-foreground font-medium leading-relaxed">
                      Use AI slug suggestions + custom OG images for higher click-through rates.
                    </p>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2.5 bg-background rounded-b-2xl shrink-0">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-border h-10">Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !url}
            className="rounded-xl gradient-primary text-white shadow-glow hover:opacity-95 h-10 gap-2 px-5 font-semibold"
            data-testid="create-link-submit-btn"
          >
            {creating ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Creating…</>
            ) : (
              <><LinkIcon size={15} weight="bold" />Create link</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
async function loadAvailableDomains(workspaceId) {
  const domains = [];

  // 1. Platform short domains (lfo.to, gfo.to, cto.to) — always first
  try {
    const platformDomains = await getPlatformDomains();
    platformDomains.forEach(d => domains.push({ domain: d.domain, label: d.label, type: 'platform' }));
  } catch { /* non-critical */ }

  // 2. Verified custom domains for this workspace
  try {
    const { data: customDomains } = await supabase
      .from('custom_domains')
      .select('domain, verified')
      .eq('workspace_id', workspaceId)
      .eq('verified', true);
    (customDomains || []).forEach(d => domains.push({ domain: d.domain, label: d.domain, type: 'custom' }));
  } catch { /* non-critical */ }

  // 3. Fallback — should not normally be reached
  if (domains.length === 0) {
    domains.push({ domain: 'lfo.to', label: 'lfo.to', type: 'platform' });
  }

  return domains;
}

function buildOgOverrides(ogData) {
  const overrides = {};
  ['twitter', 'linkedin', 'facebook'].forEach(platform => {
    if (ogData?.[platform] && Object.values(ogData[platform]).some(Boolean)) {
      overrides[platform] = ogData[platform];
    }
  });
  return Object.keys(overrides).length > 0 ? overrides : null;
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground inline-flex items-center gap-1.5 mb-2 font-['Geist_Mono',ui-monospace,monospace]">
        {Icon && <Icon size={12} />}{title}
      </span>
      {children}
    </div>
  );
}

function Field({ label, icon: Icon, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground inline-flex items-center gap-1.5 font-['Geist_Mono',ui-monospace,monospace]">
          {Icon && <Icon size={12} />}{label}
        </span>
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
